import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { contactApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner } from '../components/common';
import { formatDate } from '../utils/helpers';

const statusColors = {
  new: 'info',
  contacted: 'warning',
  converted: 'success',
  archived: 'default',
};

const statusLabels = {
  new: 'New',
  contacted: 'Contacted',
  converted: 'Converted',
  archived: 'Archived',
};

const fieldTypeIcons = {
  email: <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />,
  phone: <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />,
  text: null,
  textarea: null,
  number: null,
  date: <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />,
};

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useApp();

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('new');
  const [saving, setSaving] = useState(false);

  const fetchContact = async () => {
    try {
      const response = await contactApi.getById(id);
      setContact(response.data);
      setNotes(response.data.notes || '');
      setStatus(response.data.status);
    } catch (error) {
      showSnackbar('Failed to load contact', 'error');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await contactApi.update(id, { notes, status });
      showSnackbar('Contact updated', 'success');
      fetchContact();
    } catch (error) {
      showSnackbar('Failed to update contact', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await contactApi.delete(id);
      showSnackbar('Contact deleted', 'success');
      navigate('/contacts');
    } catch (error) {
      showSnackbar('Failed to delete contact', 'error');
    }
  };

  // Get value from contact data map
  const getContactValue = (key) => {
    if (!contact?.data) return '-';
    const value = contact.data instanceof Map ? contact.data.get(key) : contact.data[key];
    return value || '-';
  };

  // Get field mappings from API key
  const getFieldMappings = () => {
    if (contact?.apiKey?.fieldMappings?.length > 0) {
      return contact.apiKey.fieldMappings;
    }
    // If no field mappings, create defaults from data keys
    if (contact?.data) {
      const dataObj = contact.data instanceof Map ? Object.fromEntries(contact.data) : contact.data;
      return Object.keys(dataObj).map(key => ({
        externalField: key,
        displayName: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
        fieldType: key.includes('email') ? 'email' : key.includes('phone') ? 'phone' : 'text',
      }));
    }
    return [];
  };

  if (loading) {
    return <LoadingSpinner message="Loading contact..." />;
  }

  if (!contact) {
    return null;
  }

  const fieldMappings = getFieldMappings();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/contacts')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            Contact Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            From: {contact.apiKey?.name || 'Unknown Form'}
          </Typography>
        </Box>
        <Chip
          label={statusLabels[contact.status]}
          color={statusColors[contact.status]}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Form Data */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Submitted Data
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <List disablePadding>
                {fieldMappings.map((mapping, index) => {
                  const value = getContactValue(mapping.externalField);
                  const isLongText = mapping.fieldType === 'textarea' || (value && value.length > 100);

                  return (
                    <ListItem
                      key={mapping.externalField}
                      sx={{
                        px: 0,
                        py: 1.5,
                        borderBottom: index < fieldMappings.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        alignItems: isLongText ? 'flex-start' : 'center',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isLongText ? 1 : 0 }}>
                            {fieldTypeIcons[mapping.fieldType]}
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              {mapping.displayName}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="body1"
                            sx={{
                              whiteSpace: isLongText ? 'pre-wrap' : 'normal',
                              wordBreak: 'break-word',
                            }}
                          >
                            {mapping.fieldType === 'email' && value !== '-' ? (
                              <a href={`mailto:${value}`} style={{ color: 'inherit' }}>
                                {value}
                              </a>
                            ) : mapping.fieldType === 'phone' && value !== '-' ? (
                              <a href={`tel:${value}`} style={{ color: 'inherit' }}>
                                {value}
                              </a>
                            ) : (
                              value
                            )}
                          </Typography>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Status & Notes */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="contacted">Contacted</MenuItem>
                  <MenuItem value="converted">Converted</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
                Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add notes about this contact..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                size="small"
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ mt: 2 }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Submission Info
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Submitted At
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(contact.createdAt, 'MMM d, yyyy h:mm a')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Source URL
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {contact.source || 'Unknown'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography variant="body2">
                    {contact.ipAddress || 'Unknown'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    User Agent
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {contact.userAgent || 'Unknown'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Delete Button */}
          <Button
            fullWidth
            color="error"
            variant="outlined"
            onClick={handleDelete}
            sx={{ mt: 2 }}
          >
            Delete Contact
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactDetail;
